import { Container, type FederatedPointerEvent, type Ticker } from 'pixi.js';
import type { CardDef } from '../../core/card';
import { pointInPoly, type Pt } from '../card/art/geom';
import { canHoverNow, exceedsDragSlop } from '../input';
import { HOLD_HINT_MS } from '../ui/hold';
import { CardView } from '../card/cardView';
import type { CardFaceCache } from '../card/faceCache';
import type { FaceSize } from '../card/metrics';
import type { CardPose } from '../card/pose';
import { CardRig, MAX_STEP_SECONDS, type CardStep } from './cardRig';
import { approach, idlePhaseOf, type FeelParams } from './feel';
import {
  DEFAULT_CURL,
  handLayout,
  handZone,
  inHandZone,
  outIndices,
  type HandLayout,
} from './layout';

/**
 * **手牌** —— 把纯逻辑那一层接到 `CardView` 和鼠标上。
 *
 * ```
 *   指针 → 命中测试 → 谁被 hover / 谁被按住 / 点到空白
 *                            │
 *   handLayout()  按画布和牌尺寸推导出这一帧的槽位
 *   feelTarget()  每个位次的目标姿态
 *                            │
 *   chase()       位置带**最大速度**地追目标
 *   approach()    角度 / 大小单调地追目标（**不过冲、不抖**）
 *   pulseDecay()  Bar 事件：所有牌一起硬起一下、easeout 落回
 *                            ▼
 *                       card.setPose()
 * ```
 *
 * 手感本身**一行都不在这里** —— 全在 `feel.ts` / `layout.ts`，那两份是纯的、
 * 可单测的。每张牌的姿态动画在 `cardRig.ts`（`CardRig`），**正中那张异常牌用的是
 * 同一个 rig**（评审：不要重复实现功能）。这个文件只管四件事：
 *
 * 1. **命中测试 + 顺序表 `#order`**（谁在扇形里的第几位）
 * 2. 每张牌该摆成什么样（扇形槽位 + 整扇上移 + 邻居避让两个包络）
 * 3. 指针交互：hover / 按下 / **点选** / **拖动换位** / **拖出去打出**
 * 4. 把结果交给 `CardRig`（包络 + 槽位 → 真正的姿态）
 *
 * ## ⚠️ 为什么自己做命中测试，不用 Pixi 的事件系统
 *
 * 因为这个扇形里的牌是**假四边形**（`PerspectiveMesh` 的顶点被改过）、
 * 互相叠着、还一直在动。我们有每张牌精确的四角（`CardView.corners`），
 * 用它做点在多边形内测试既准又便宜，而且**层叠顺序由我们自己说了算** ——
 * 把牌拖到另一张上面时，"抓到的是上面那张"必须和**看到的**一致。
 *
 * ## ⚠️ 输入要求
 *
 * `input` 上必须能收到指针事件：`eventMode = 'static'` 并且有一个铺满的
 * `hitArea`（`/debug/hand.html` 和 `cardLab.ts` 都是这么设的）。
 * `HandView` 不替调用方改这两个值 —— 那是场景的布局，不是手牌的事。
 */

export interface HandViewOptions {
  /** 在哪收指针事件。一般就是 `app.stage` */
  readonly input: Container;
  readonly faces: CardFaceCache;
  /** 牌本身的逻辑尺寸（不含出血） */
  readonly size: FaceSize;
  /** 手牌里都有哪些牌，按初始顺序 */
  readonly cards: readonly CardDef[];
  /** 逻辑画布尺寸。⚠️ 手牌摆在哪儿是由它和牌尺寸**推导**出来的 */
  readonly bounds: { readonly width: number; readonly height: number };
  /**
   * 手动指定扇形锚点。
   *
   * ⚠️ 不给就按评审的规则推导：x 居中、y 让中心牌的底边贴着画布底边。
   * 调试页那张"单张特写"才需要手动指 —— 它不在手牌位置上。
   */
  readonly anchor?: { readonly x: number; readonly y: number };
  /** 弯多少（单位：牌高）。见 `layout.ts` */
  readonly curl?: number;
  /** 手牌横向占画布的比例 */
  readonly fill?: number;
  readonly feel?: Partial<FeelParams>;
  /**
   * **拖出手牌范围松手**时回调。参数是打出的牌（可能多张：拖的是选中的一组）。
   *
   * ⚠️ 回调里**不要**直接调 `setCards()` —— 那是"重建整手牌"，
   * 会把正在飞的动画打断。这里只负责把牌停在落点、从顺序表里摘掉；
   * 手牌真正的增减由上层状态驱动（`architecture.md` §7.4 的 keyed diff）。
   */
  readonly onPlay?: (played: readonly CardDef[]) => void;
  /** 点击空白处是否清空选中。默认 true */
  readonly clearOnEmptyClick?: boolean;
  /**
   * 拖出多高才算"打出去"（**单位：牌高**，从手牌顶边再往上多少）。默认 0.27
   *
   * ⚠️ 和 `FeelParams` 里那些长度一样是**比例**，不是像素。
   */
  readonly playMargin?: number;
}

/**
 * 按下之后移动超过这么多像素，就算“拖”而不是“点”。
 *
 * ⚠️ 阈值随输入画像走（手指比鼠标抖），见 `render/input/drag.ts`。
 */

/**
 * **整扇上移 / 邻居避让"落回去"之前等多久**（毫秒）。
 *
 * 评审：「取消 hover 时 debounce 半秒左右，防止反复触发」，随后又按手感收到
 * **350ms**（"debounce 过头了"）。鼠标从一张牌滑到另一张时会扫过牌缝，
 * `#hoveredCard` 闪一下 -1；没有这个等待，整扇和邻居就会跟着抖一下。
 *
 * ⚠️ 抬起来那一下仍然是瞬时的（评审："手牌扇上抬无动画，归位才有动画"）。
 * ⚠️ **整扇上移**（`#stepGroup`）和**邻居避让**（`#stepAvoid`）各记各的等待，
 *    共用一个计时的话，指针扫过牌缝会让其中一个提前落回去。
 */
const GROUP_HOLD_MS = 350;


export class HandView {
  /** 挂进场景的东西 */
  readonly view: Container;

  /**
   * 每张牌的节点。索引 = `cards` 里的索引，**不是**扇形里的位次。
   *
   * ⚠️ 牌由 `CardRig` 持有（它是唯一会往 `view` 上挂牌的地方）——
   *    这里只是转出来给外面读。
   */
  get hands(): readonly CardView[] {
    return this.#rig.cards;
  }

  /**
   * **牌怎么动**（姿态动画、包络、音乐脉动）。
   *
   * ⚠️ 正中那张异常牌用的是**同一个类**（`CardRig`），只是它的 owner 换成
   *    `render/game/centeredCard.ts` —— 评审要的就是这个（"不要重复实现功能"）。
   */
  readonly #rig: CardRig;

  #curl: number;
  #fill: number | undefined;
  #anchor: { x: number; y: number } | undefined;
  #playMargin: number;
  #clearOnEmptyClick: boolean;
  #onPlay: ((played: readonly CardDef[]) => void) | undefined;

  readonly #input: Container;
  readonly #faces: CardFaceCache;
  readonly #size: FaceSize;
  #bounds: { readonly width: number; readonly height: number };
  #defs: CardDef[];

  /**
   * **扇形里的位次 → 卡牌索引。** `#order[i]` 是"第 i 位摆的是哪张牌"。
   *
   * ⚠️ 换位（拖动调序）改的是**它**，不是 `hands` 数组 ——
   * `hands` 的索引回答的是"这是哪张牌"，和摆在哪无关。
   */
  #order: number[] = [];

  /** 被选中的**卡牌索引** */
  readonly #selected = new Set<number>();
  /** 已经打出去的卡牌索引。它们在顺序表外面，也不参与命中测试 */
  readonly #played = new Set<number>();
  /**
   * **给一张正在飞过来的牌预留的空位个数**（0 或 1）。
   *
   * 评审：
   *
   * > 飞入手牌的效果应当是新牌和手牌的代码协同的。手牌代码告诉新牌目的地
   * > 坐标，旋转角度，尺寸等所有参数，然后自身有一个加牌时的动画令其他手牌
   * > 让出一个空位；新牌到位后告诉手牌代码，令其渲染为添加完牌后的状态并
   * > 删除新牌。
   *
   * 做法就是**把手牌当成"已经多了一张"来排**：`#computeLayout` 多算一格，
   * 于是所有现有的牌都往新的位置挪（它们本来每帧就在追自己的槽位，所以
   * "让出一个空位"是免费的），而最后那一格空着 —— 它的姿态就是
   * `reserveArrival()` 交给那张飞过来的牌的。
   */
  #arrival = 0;

  /**
   * **暂时不显示的卡牌索引。**
   *
   * 只有一个用处：抽牌动画期间，那张牌正从抽牌堆飞过来 —— 它此刻**已经在
   * 手里了**（状态里已经有了），但画面上还没到。不藏的话，牌堆那儿飞着一张、
   * 手牌槽里同时已经坐着一张，看起来是抽了两张。
   *
   * ⚠️ 位置照样占着（`#layout` 不变），所以落点就是它将来待着的那个槽位 ——
   * 动画的终点从 `Hand` 现读，不需要第二套布局。
   */
  readonly #hidden = new Set<number>();
  /**
   * **打出去之前那一手的样子。** 只有 `#play()` 写它，`revert()` 读它。
   */
  #undo: { order: number[]; played: number[]; z: Array<[number, number]> } | null = null;

  #pointer: [number, number] | null = null;
  #hoveredCard = -1;
  /** 触控长按「钉住」的那一张（-1 = 没有）。它优先于指针命中，直到下一次按下 */
  #pinnedCard = -1;
  /** 当前这一次按住的时长（毫秒），松手 / 拖动 / 换指针就归零 */
  #pressHoldMs = 0;
  /** 这一次按住已经达到长按阈值了吗（松手时据此跳过点选） */
  #longPressed = false;
  /**
   * **整扇上移 / 邻居避让"落回去"之前的等待**（毫秒）。
   *
   * 评审：「取消 hover 时 debounce 半秒左右，防止反复触发」——鼠标从一张牌滑到
   * 另一张时中间会扫过牌缝，`#hoveredCard` 会闪一下 -1；没有这个等待的话，
   * 整扇和邻居就会跟着上下抖一下。
   *
   * ⚠️ 抬起来那一下仍然是**瞬时**的（评审："将 hover 时牌扇上抬的动画删去"），
   *    等待只加在"要不要落回去"这一侧。
   */
  #groupHoldMs = 0;
  #avoidHoldMs = 0;
  /**
   * **下一次 `update` 时，所有牌直接到位**（重建之后的头一帧）。
   *
   * 评审："当我按下回合结束抽牌时，手牌扇仍然抖动了。我希望刷新时若检测到
   * hover，就直接到位，避免抖动。"
   *
   * 病根：`setCards()` 会**重建**整手牌（牌数一变，`settleInherit` 那条"接上
   * 旧状态"的路就走不了），于是每张牌从槽位出发、再 `chase` 到"抬起来的槽位"——
   * 手指正好在手牌区里时，那一下就是"掉下去再抬起来"。
   * 重建之后的头一帧把它们**直接摆到目标位置**，就一个像素都不抖。
   */
  #snapNextFrame = false;
  /**
   * **现在该不该听 hover。**
   *
   * 评审第六轮："干脆让牌扇仅在可操作（回合进行中，打出断点，或类似情形）时
   * 监听 hover，其他时候无视 hover"。
   *
   * 不只是"少一点动效"：**刷新（`setCards` 重建）正好发生在抽牌 / 出牌之后**，
   * 而那时指针往往还压在手牌区里 —— 不可操作的阶段还跟着 hover 抬来抬去，
   * 就总会有"掉下去又抬起来"的观感。判定权在屏幕（它知道现在轮到谁、能不能出牌），
   * 这里只负责"听不听"。
   *
   * ⚠️ 它只管**视觉**（hover 抬起 / 整扇上移）：点选、拖动、命中测试照旧 ——
   *    那些由 `tableIntent` 那一层判合法性。
   */
  #interactive = true;
  /**
   * **远端点名要看的那一张**（卡牌索引）。-1 = 没有。
   *
   * 评审：「抽牌人 hover 一张牌背时，被抽牌人那边也应当看到自己对应的牌
   * 因被对方 hover 而上移且放大」—— 所以它是"对方的手指点在我这张上"，
   * 优先于本地指针（本地没指着任何一张时它才生效）。
   */
  #remoteHover = -1;
  /**
   * **整扇上移的包络** 0..1（评审：「手牌扇的上移改为忽略 x 坐标，仅检测玩家
   * 鼠标 y 坐标是否在底部手牌扇占据的一整个区域内」）。
   *
   * ⚠️ 它**只看 y**：指针落在手牌那条横带里就抬，和 x 无关（牌缝里、扇形两边的
   * 空处也算）。"哪一张被指着"是另一件事，仍走逐张命中测试。
   *
   * ⚠️ **进是瞬时的**（评审：「hover 手牌时的动画时间改为 0」）：抬起来不带
   * 补间，落回去照旧走 `fallRate`。
   */
  #group = 0;
  /**
   * **邻居避让的包络** 0..1。它和 `#group` 分开：评审明说了「解除 Hover 后归位
   * 动画以及其他牌的避让行为不受影响」—— 上移改成瞬时**不能**把避让也带着瞬移。
   */
  #avoid = 0;
  /** 指针底下按住的那张牌。-1 = 按在空白处 */
  /** 能不能把手牌**打出去**。false = 只能拖拽换位（迷惑对手），不能打出 */
  #playable = true;
  #pressedCard = -1;
  /** 这次按下拖动了没有（区分"点选"和"拖动"） */
  #dragged = false;
  #downAt: [number, number] = [0, 0];
  /** 拖动时每张参与牌的"牌心 − 指针"偏移，免得一抓就跳 */
  #grab = new Map<number, [number, number]>();
  /** 这一次拖动带着哪几张（单选拖一张，选中多张就带整组） */
  #dragSet: number[] = [];
  #layout: HandLayout;

  constructor(options: HandViewOptions) {
    this.#input = options.input;
    this.#faces = options.faces;
    this.#size = options.size;
    this.#bounds = options.bounds;
    this.#defs = [...options.cards];
    this.#curl = options.curl ?? DEFAULT_CURL;
    this.#fill = options.fill;
    this.#anchor = options.anchor;
    this.#playMargin = options.playMargin ?? 0.27;
    this.#clearOnEmptyClick = options.clearOnEmptyClick ?? true;
    this.#onPlay = options.onPlay;

    this.view = new Container();
    this.view.label = 'hand';
    // ⚠️ 排序要开：被 hover / 被抓住的牌要能整体换到最上面
    this.view.sortableChildren = true;
    /**
     * ⚠️ 牌**直接挂在 `view` 上**（由 rig 代管）—— 场景里 `label = 'hand'` 那个
     *    节点下面仍然是一排 `CardView`，调试页/冒烟/读数都按那个形状找牌。
     */
    this.#rig = new CardRig({
      view: this.view,
      ...(options.feel === undefined ? {} : { feel: options.feel }),
    });

    this.#layout = this.#computeLayout(0);
    this.#rebuild();
    this.#attach();
  }

  // ---- 参数与读数 ----

  get feel(): Readonly<FeelParams> {
    return this.#rig.feel;
  }

  /** 这一帧推导出来的摆法（间距/垂度/锚点）。调试页拿它显示读数 */
  get layout(): Readonly<HandLayout> {
    return this.#layout;
  }

  /** 改手感参数。立刻生效 */
  applyFeel(next: Partial<FeelParams>): void {
    this.#rig.applyFeel(next);
  }

  /**
   * **音乐脉动（Bar 事件）**：所有牌**立即**放大一下，然后 easeout 缩回去。
   *
   * ⚠️ 放大是硬赋的、不走平滑 —— 评审要的是"无动画地立即稍微放大"。
   * 只有缩回去有动画（指数衰减）。
   *
   * ⚠️ 现在由调试页上一个按钮手动触发。接进主界面之后应当由
   * `debug/bgm.html` 那套 `BgmDirector` 的 Bar 事件驱动 —— 那时把这个方法
   * 挂到那个事件上就行，**不要**在这里 import 音频层。
   */
  pulse(): void {
    this.#rig.pulse();
  }

  /** 当前脉动值 0..1（调试读数用） */
  get pulseValue(): number {
    return this.#rig.pulseValue;
  }

  /**
   * 画布尺寸变了。
   *
   * ⚠️ 摆法（弧心、间距、垂度）全是从 `bounds` 推出来的，所以窗口一变就得调它 ——
   * 调用方不需要自己算任何位置。下一帧生效。
   */
  setBounds(bounds: { readonly width: number; readonly height: number }): void {
    this.#bounds = { width: bounds.width, height: bounds.height };
    this.#layout = this.#computeLayout(this.hands.length + this.#arrival);
  }

  /** 改弯度 / 横向占比 */
  applyLayout(next: { curl?: number; fill?: number }): void {
    if (next.curl !== undefined) this.#curl = next.curl;
    if (next.fill !== undefined) this.#fill = next.fill;
  }

  /**
   * **告诉手牌现在能不能操作**（见 `#interactive`）。
   *
   * 关掉时 hover 立刻清空（牌按 `fallRate` 落回去，不是"啪"地掉），
   * 整扇上移也跟着收回去。
   */
  setInteractive(on: boolean): void {
    if (on === this.#interactive) return;
    this.#interactive = on;
    if (!on) {
      /**
       * ★ **不可操作时跳过 debounce，立刻走"归位"那一段动画。**
       *
       * 评审："`hand.setInteractive` 设 false 时，跳过 debounce 播放 unhover
       * 的归位动画"。
       *
       * 那个 350ms 是给"指针在牌缝里扫过"用的（防止整扇上移抖一下）；
       * 而"这回合结束了 / 轮不到我"不是抖，是**状态变了** —— 再等 350ms
       * 只会让画面迟一拍。跳过等待之后走的仍然是 `approach` 补间
       * （归位是动画，不是"啪"地掉）。
       */
      this.#groupHoldMs = 0;
      this.#avoidHoldMs = 0;
    }
  }

  /** 现在听不听 hover / 拖拽重排（见 `#interactive`）。诊断与冒烟用 */
  get interactive(): boolean {
    return this.#interactive;
  }

  /**
   * **能不能把牌打出去。** 关掉后仍然可以拖拽换位（迷惑对手），
   * 但拖过出牌线不会打出 —— 牌自己弹回原位。
   */
  setPlayable(on: boolean): void {
    this.#playable = on;
  }

  /** 现在能不能把牌打出去（诊断用）。见 setPlayable */
  get playable(): boolean {
    return this.#playable;
  }

  /** 现在被 hover 的是哪张牌（卡牌索引）。-1 = 没有 */
  get hovered(): number {
    return this.#hoveredCard;
  }

  /** 现在被 hover 的那张牌的**定义**。没指着任何一张就是 `null` */
  get hoveredDef(): CardDef | null {
    return this.#hoveredCard >= 0 ? (this.#defs[this.#hoveredCard] ?? null) : null;
  }

  /**
   * **某张牌在画布上的包围盒。**
   *
   * ⚠️ `CardView.corners` 是**相对牌心**的（见 `cardView.ts` 的文件头），
   *    所以要加上那张牌的 `position` 才是画布坐标 —— 少了这一步，
   *    悬停说明会飘到画面左上角去。
   */
  boxOf(cardIndex: number): { x: number; y: number; width: number; height: number } | null {
    const card = this.hands[cardIndex];
    if (card === undefined) return null;
    const c = card.corners;
    const xs = [c.x0, c.x1, c.x2, c.x3];
    const ys = [c.y0, c.y1, c.y2, c.y3];
    const minX = Math.min(...xs) + card.position.x;
    const minY = Math.min(...ys) + card.position.y;
    const maxX = Math.max(...xs) + card.position.x;
    const maxY = Math.max(...ys) + card.position.y;
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  /** 现在藏起来的那些牌（卡牌索引）。诊断用，见 `setHidden` */
  get hidden(): readonly number[] {
    return [...this.#hidden];
  }

  /**
   * **远端**点名要看哪一张（`null` = 不看了）。
   *
   * 只有"本地指针没指着任何一张"时它才生效 —— 否则对方的手会把我自己
   * 的指针顶掉，那是反的。
   */
  setRemoteHover(cardIndex: number | null): void {
    this.#remoteHover = cardIndex ?? -1;
  }

  /**
   * **现在被拎起来多远**（相对它自己的静止位置），单位是**牌高**。
   *
   * ⚠️ 用"牌高"而不是像素：两边的牌一样大时它才等于像素，
   * 而不一样大时按比例还原 —— 于是"逐帧重现拖拽"在两种尺寸下都成立。
   * 联机时这个值直接进 `net/side.ts` 的 `DragFrame`。
   */
  dragInfo(): {
    readonly card: number;
    readonly dx: number;
    readonly dy: number;
    /**
     * **绝对位置**（相对扇形中心，单位 = 牌高）。重放拖拽用它，**不用 dx/dy**：
     * 换位会让 `rest` 跳，相对值跟着跳 —— 远端那张就原地抽搐。
     */
    readonly x: number;
    readonly y: number;
    readonly armed: boolean;
  } | null {
    const i = this.#pressedCard;
    if (i < 0) return null;
    const st = this.#rig.state(i);
    if (st === undefined) return null;
    const unit = this.#size.height;
    return {
      card: i,
      dx: (st.pos[0] - st.rest[0]) / unit,
      dy: (st.pos[1] - st.rest[1]) / unit,
      x: (st.pos[0] - this.#layout.centerX) / unit,
      y: (st.pos[1] - this.#layout.centerY) / unit,
      // 松手会不会打出：和 `#release` 用的是同一条线
      armed: this.#dragTarget(i)[1] < this.#playLineY(),
    };
  }

  /** 现在被按住的是哪张牌（卡牌索引）。-1 = 没有 */
  get held(): number {
    return this.#pressedCard;
  }

  /**
   * **拖动期间，现在"越过出牌线"的那几张**（松手就会打出去）。
   *
   * 和 `#release` 用的是同一条线、同一个判据 —— 上层据此在**松手之前**
   * 就把这一手"举起来"（开打断窗口），而不是等松手才提交。
   */
  dragOut(): readonly CardDef[] {
    if (this.#pressedCard < 0) return [];
    return outIndices(this.#dragSet, (i) => this.#dragTarget(i)[1], this.#playLineY())
      .map((i) => this.#defs[i])
      .filter((def): def is CardDef => def !== undefined);
  }

  /** 第 i 张牌的定义（观众广播"按下露牌"用）。越界返回 null */
  defAt(index: number): CardDef | null {
    return this.#defs[index] ?? null;
  }

  /** 被选中的卡牌索引，从小到大 */
  get selected(): readonly number[] {
    return [...this.#selected].sort((a, b) => a - b);
  }

  /** 已经打出去的卡牌索引 */
  get played(): readonly number[] {
    return [...this.#played].sort((a, b) => a - b);
  }

  /** 扇形里第 i 位摆的是哪张牌 */
  get order(): readonly number[] {
    return this.#order;
  }

  get count(): number {
    return this.hands.length;
  }

  isSelected(cardIndex: number): boolean {
    return this.#selected.has(cardIndex);
  }

  toggleSelect(cardIndex: number): void {
    if (this.#played.has(cardIndex)) return;
    if (this.#selected.has(cardIndex)) this.#selected.delete(cardIndex);
    else this.#selected.add(cardIndex);
  }

  clearSelection(): void {
    this.#selected.clear();
  }

  selectAll(): void {
    for (let i = 0; i < this.hands.length; i++) {
      if (!this.#played.has(i)) this.#selected.add(i);
    }
  }

  /**
   * 换手牌（张数也可以变）。
   *
   * ⚠️ 会**重建**所有 `CardView`（尺寸不同就没法复用）并清掉选中/顺序/已打出。
   */
  setCards(cards: readonly CardDef[]): void {
    this.#defs = [...cards];
    this.#rebuild();
  }

  /**
   * 一切归零：所有牌瞬间回到槽位，顺序复位，选中/已打出清空。
   *
   * 调参时很有用：牌被拖得到处都是之后，不用等它自己飞回来。
   */
  reset(): void {
    this.#pointer = null;
    this.#hoveredCard = -1;
    this.#pinnedCard = -1;
    this.#pressHoldMs = 0;
    this.#longPressed = false;
    this.#pressedCard = -1;
    this.#dragged = false;
    this.#selected.clear();
    this.#played.clear();
    this.#hidden.clear();
    this.#undo = null;
    this.#arrival = 0;
    this.#group = 0;
    this.#avoid = 0;
    this.#grab.clear();
    this.#dragSet = [];
    this.#order = this.hands.map((_, i) => i);
    this.#layout = this.#computeLayout(this.hands.length + this.#arrival);

    for (let i = 0; i < this.hands.length; i++) {
      const slot = this.#layout.slots[i];
      const card = this.hands[i];
      if (card !== undefined) card.visible = true;
      if (slot === undefined) continue;
      // ⚠️ 瞬移回槽位（不留补间、包络清零）—— 见 `CardRig.settle`
      this.#rig.settle(i, slot);
    }
  }

  /**
   * **藏起 / 显示若干张牌**（卡牌索引）。抽牌动画期间用，见 `#hidden`。
   *
   * 传进来的这一组会**替换**掉上一组 —— 它是"现在哪些不该显示"的完整答案，
   * 不是增量的开关（增量的话忘了清就是"某张牌永远看不见"）。
   */
  setHidden(cards: readonly number[]): void {
    this.#hidden.clear();
    for (const i of cards) this.#hidden.add(i);
    for (let i = 0; i < this.hands.length; i++) {
      const card = this.hands[i];
      if (card !== undefined) card.visible = !this.#hidden.has(i);
    }
  }

  /**
   * **给一张正在飞过来的牌留个位，并把它该落的那一格交给调用方。**
   *
   * 返回值是**完整姿态**：坐标、旋转（`roll`）、尺寸……也就是评审说的
   * "坐标，旋转角度，尺寸等所有参数"。调用方把它交给那张飞过来的牌，
   * 牌到位之后再 `commitArrival()`。
   *
   * ⚠️ 它**不碰 `#defs`** —— 新牌此刻还不在手牌里（调用方在喂手牌的时候
   * 也要把它滤掉），这正是"新牌到位后手牌才渲染为加完牌的状态"。
   */
  reserveArrival(): CardPose | null {
    const count = this.hands.length;
    this.#arrival = 1;
    this.#layout = this.#computeLayout(count + 1);
    return this.#layout.slots[count] ?? null;
  }

  /**
   * **新牌到位。** 把预留那一格坐实 —— 调用方紧接着 `setCards()` 把新牌
   * 喂进来（张数从 n 变成 n+1 之后，布局和刚才预留的**是同一个**，
   * 所以画面上不会有第二次位移）。
   */
  commitArrival(): void {
    this.#arrival = 0;
  }

  /** 放弃这次让位（牌没飞过来）：把牌收回去，别留一个空位 */
  cancelArrival(): void {
    if (this.#arrival === 0) return;
    this.#arrival = 0;
    this.#layout = this.#computeLayout(this.hands.length);
  }

  /**
   * **把上一手打出去的牌放回手上。** 返回有没有真的退回去。
   *
   * 评审：「打出手牌的动作不会验证动作是否合法，也不会适时同步和回退状态。」
   *
   * `#play()` 是乐观的：它把牌从扇形里摘掉、标记成已打出、然后**才**通知
   * 上层。上层（热座 / 联机）要拿这一手去 `options()` 里找对应的合法动作 ——
   * 找不到、或者被 reducer 拒了，就得回到"拖出去之前"。
   *
   * 回到的是**快照**（顺序表 + 已打出的集合 + 那些牌的 zIndex），不是重新排
   * 一遍 —— 重新排会丢掉玩家自己理过的顺序。
   *
   * ⚠️ 状态**真的**变了之后（`setCards` 重建）这个快照会被丢掉，
   * 所以一次成功的出牌之后再调它是空操作。
   */
  revert(): boolean {
    const undo = this.#undo;
    if (undo === null) return false;
    this.#order = [...undo.order];
    this.#played.clear();
    for (const i of undo.played) this.#played.add(i);
    for (const [i, z] of undo.z) {
      const card = this.hands[i];
      if (card !== undefined) card.zIndex = z;
    }
    this.#undo = null;
    // 选择状态也可能引用了已经挪过位的下标 —— 一并清掉，别留下幽灵选中
    this.#selected.clear();
    return true;
  }

  // ---- 每帧 ----

  /**
   * 推进一帧。
   *
   * ⚠️ 和背景、濒死层一样，这个方法给**那唯一一个** ticker 回调调
   * （`architecture.md` §7.1），不要自己再 `app.ticker.add`。
   */
  update(ticker: Ticker): void {
    // ⚠️ `deltaMS` 要**夹住**：切走标签页再回来，它可能是好几秒。
    //    不夹的话牌会瞬移、"最大速度"那一整套限制会被绕过，
    //    弹簧也会因为一步跨得太大而炸掉
    const dt = Math.min(ticker.deltaMS / 1000, MAX_STEP_SECONDS);
    const n = this.hands.length;
    if (n === 0) return;
    this.#layout = this.#computeLayout(n + this.#arrival);

    // ⚠️ 按住的时候 hover 锁在被按住那张上 —— 拖到牌堆外面时
    //    整手牌不该突然塌回原位
    // 按住 > 本地指着 > 对方点名要看
    // ⚠️ 不可操作时**无视指针**（见 `#interactive`）：按住的牌、对方点名要看的
    //    那几张仍然照旧（那是"正在进行的事"，不是 hover 带来的）
    // ⚠️ 只有真能 hover 的指针才做本地命中。触控点完一次之后浏览器还会把
    //    指针位置留在原地，如果照它算 hover，整扇和邻居会一直保持抬起 / 让位。
    const canHover = canHoverNow();
    if (canHover) {
      // 鼠标/笔能 hover：不需要长按钉住
      this.#pinnedCard = -1;
      this.#pressHoldMs = 0;
      this.#longPressed = false;
    } else if (this.#pressedCard >= 0 && !this.#dragged) {
      // 触控：按住不动一会儿 = 想看这张牌的说明（长按）。拖动则不算
      this.#pressHoldMs += ticker.deltaMS;
      if (this.#pressHoldMs >= HOLD_HINT_MS && this.#pinnedCard !== this.#pressedCard) {
        this.#pinnedCard = this.#pressedCard;
        this.#longPressed = true;
      }
    }
    const localHit = this.#interactive && canHover ? this.#hitTest(this.#pointer) : -1;
    this.#hoveredCard =
      this.#pinnedCard >= 0
        ? this.#pinnedCard
        : this.#pressedCard >= 0
          ? this.#pressedCard
          : localHit >= 0
            ? localHit
            : this.#remoteHover;

    // 拖动换位：只在"拖单张、而且还在手牌区里"的时候做。
    // 拖到出牌区（上面）时不换位 —— 那是在把牌打出去，不是整理
    if (this.#dragged && this.#dragSet.length === 1 && this.#pointer !== null) {
      const card = this.#dragSet[0]!;
      const target = this.#dragTarget(card);
      if (target[1] >= this.#playLineY()) this.#reorderTo(card, target[0]);
    }

    const hoveredPos = this.#posOf(this.#hoveredCard);

    /**
     * **整扇上移：只看 y。**
     *
     * 评审：「手牌扇的上移改为忽略 x 坐标，仅检测玩家鼠标 y 坐标是否在底部手牌扇
     * 占据的一整个区域内。」所以判据是那条**通栏横带**（`handZone`），不是"指针
     * 压在某张牌上" —— 牌缝里、扇形两边都算在手牌区里。
     *
     * ⚠️ 进是瞬时的（`wantGroup > this.#group` 时直接赋值），出按 `fallRate`。
     */
    const zone = handZone(
      this.#layout,
      this.#size,
      (this.#rig.feel.fanLift + this.#rig.feel.hoverLift) * this.#size.height,
    );
    const wantGroup =
      this.#interactive && canHover && this.#pointer !== null && inHandZone(this.#pointer[1], zone) ? 1 : 0;
    const groupWas = this.#group;
    this.#group = this.#stepGroup(wantGroup, dt);
    /**
     * ★ **整扇上移是瞬时的**：包络跳上去还不够 —— 每张牌的**目标位置**也跟着
     * 抬了，而位置是 `CardRig` 用 `chase` 补间过去的。所以"抬起那一帧"要顺手
     * 告诉 rig：这一帧的位置**直接到位**（评审："手牌扇上抬无动画，归位才有
     * 动画"）。归位那一路（`#group` 落回去）照旧补间。
     */
    const groupRose = this.#group > groupWas + 0.001;
    // ★ 重建之后的头一帧：全部直接到位（见 `#snapNextFrame`）
    const snapAll = this.#snapNextFrame;
    this.#snapNextFrame = false;

    /**
     * **邻居避让照旧补间**（评审：「其他牌的避让行为不受影响」）。
     *
     * 它跟着"有没有牌被指着"走，进/出都按手感那两条速率 —— 和上面那个瞬时的
     * 整扇上移是**两个独立的包络**。
     */
    const wantAvoid = this.#hoveredCard >= 0 ? 1 : 0;
    this.#avoid = this.#stepAvoid(wantAvoid, dt);

    /**
     * ⚠️ **姿态本身交给 `CardRig`**（正中那张异常牌用的是同一个类）。
     *
     * 这里只负责"这一张该摆成什么样 + 被指着没有 + 拖着就跟着指针"，
     * 也就是扇形的规则；包络、平滑、空闲摇摆、脉动全在 rig 那一侧。
     */
    const specs: CardStep[] = [];
    for (let i = 0; i < n; i++) {
      const card = this.hands[i];
      // 藏起来的那几张：不画（位置照样占着，见 `#hidden`）
      if (card !== undefined) {
        card.visible = !this.#hidden.has(i);
        // ★ 选中态的外发光：每帧同步目标状态，再按 350ms 补间透明度
        card.setGlow(this.#selected.has(i));
        card.stepGlow(ticker.deltaMS);
      }

      const pos = this.#posOf(i);
      const slot = this.#layout.slots[pos] ?? this.#layout.slots[0];
      if (slot === undefined) continue;

      const dragging = this.#dragSet.includes(i);
      // ⚠️ 被拖动的牌**保持直立**：抛开扇形弧线带来的倾角和左右转向，
      //    只留整手牌共同的那一点前倾。不然从扇形里拎出来的牌是歪的，
      //    读不清牌面 —— 而拖动正是"我要看这张牌"的时刻
      const posture: CardPose = dragging
        ? { ...slot, roll: 0, yaw: 0, pitch: this.#layout.basePitch }
        : slot;

      /**
       * ⚠️ 打出去的牌**不推**（`specs` 里留空，rig 会跳过这一格）——
       *    它就停在落点，不再参与任何动画。
       */
      if (this.#played.has(i)) continue;

      specs[i] = {
        posture,
        input: {
          phase: idlePhaseOf(pos, this.#rig.feel),
          index: pos,
          hovered: hoveredPos,
          selected: this.#selected.has(i),
          pointer: this.#pointer,
          unit: this.#size.height,
          wide: this.#size.width,
        },
        wantHover: i === this.#hoveredCard ? 1 : 0,
        wantPress: i === this.#pressedCard ? 1 : 0,
        // ★ 整扇抬起那一帧 / 重建后的头一帧：位置**直接到位**
        //    （见 `groupRose` 与 `#snapNextFrame`）
        ...(groupRose || snapAll ? { snapPos: true } : {}),
        // 拖动中：目标是指针 + 抓住时的偏移（"手指底下的牌就该在手指底下"）
        ...(dragging ? { override: this.#dragTarget(i) } : {}),
        // 被抓住的最前，其次是被 hover 的，再按位次
        z: dragging ? 3000 : i === this.#hoveredCard ? 2000 : pos,
      };
    }
    this.#rig.step(dt, this.#group, this.#avoid, specs);
  }

  destroy(): void {
    this.#detach();
    // ⚠️ 牌挂在 rig 那个容器里，销毁 `view` 会连带销毁它 —— 这里是摘引用
    this.#rig.clear();
    this.view.destroy({ children: true });
  }

  // ---- 内部：布局与重建 ----

  #computeLayout(count: number): HandLayout {
    const layout = handLayout({
      bounds: this.#bounds,
      card: this.#size,
      count,
      curl: this.#curl,
      ...(this.#fill === undefined ? {} : { fill: this.#fill }),
    });
    // 调试页那张单张特写要摆在画面中间，不跟着"贴底居中"那套走
    if (this.#anchor === undefined) return layout;
    const dx = this.#anchor.x - layout.centerX;
    const dy = this.#anchor.y - layout.centerY;
    return {
      ...layout,
      centerX: layout.centerX + dx,
      centerY: layout.centerY + dy,
      slots: layout.slots.map((s) => ({ ...s, x: s.x + dx, y: s.y + dy })),
    };
  }

  /**
   * **整扇上移的包络**：抬起来**瞬时**，落回去先等 `GROUP_HOLD_MS` 再补间。
   *
   * 评审："将 hover 时牌扇上抬的动画删去" + "取消 hover 时 debounce 半秒左右"。
   */
  #stepGroup(want: number, dt: number): number {
    if (want > 0) {
      this.#groupHoldMs = GROUP_HOLD_MS;
      return 1;
    }
    if (this.#groupHoldMs > 0) {
      this.#groupHoldMs -= dt * 1000;
      return this.#group;
    }
    return approach(this.#group, 0, dt, this.#rig.feel.fallRate);
  }

  /**
   * **邻居避让的包络**：进来 / 出去**都是补间**（它有自己那两条速率）。
   *
   * ⚠️ 这里**不是瞬时的** —— 评审："避让动画稍微慢一点，避让幅度大了之后会显得
   *    太快"。落回去也等 `GROUP_HOLD_MS`（同一份 debounce：指针扫过牌缝时
   *    邻居不该跟着抖一下），等待期间**保持原值**、不动。
   */
  #stepAvoid(want: number, dt: number): number {
    if (want > 0) this.#avoidHoldMs = GROUP_HOLD_MS;
    if (want <= 0 && this.#avoidHoldMs > 0) {
      this.#avoidHoldMs -= dt * 1000;
      return this.#avoid;
    }
    const feel = this.#rig.feel;
    return approach(
      this.#avoid,
      want,
      dt,
      want > this.#avoid ? feel.avoidRiseRate : feel.avoidFallRate,
    );
  }

  #rebuild(): void {
    // ★ 重建之后头一帧全部直接到位（见 `#snapNextFrame`）
    this.#snapNextFrame = true;
    /**
     * ★ **把上一帧每张牌的状态先抄下来。**
     *
     * 评审报的："cardView 刷新时牌扇会失去 hover，无动画地回到初始位置，
     * 并重新触发上抬动画。" —— 根因就是重建把位置 / 大小 / hover 包络清零了。
     * 位次对得上时（最常见的那种刷新：同一手牌重建）接上旧状态，于是
     * 下一帧从**当前所在的地方**继续追目标，既不跳、也不重放上抬。
     */
    const prevStates = this.#rig.cards.length === this.#defs.length ? this.#rig.snapshot() : [];
    // ⚠️ 顺序：先把 rig 里那批牌摘掉并销毁，再挂新的
    for (const card of this.#rig.cards) card.destroy({ children: true });
    this.#rig.clear();
    this.#selected.clear();
    this.#played.clear();
    this.#hidden.clear();
    this.#undo = null;
    this.#grab.clear();
    this.#dragSet = [];

    for (let i = 0; i < this.#defs.length; i++) {
      const def = this.#defs[i];
      if (def === undefined) continue;
      const card = new CardView({ texture: this.#faces.get(def, this.#size), size: this.#size });
      // ⚠️ 卡牌节点自己不收事件：命中测试是我们自己做的（见文件头）。
      //    留着 'auto' 只会让 Pixi 每帧对几十个顶点做一遍没人用的命中测试
      card.eventMode = 'none';
      this.#rig.add(card);
    }
    this.#order = this.hands.map((_, i) => i);
    this.#layout = this.#computeLayout(this.hands.length + this.#arrival);
    for (let i = 0; i < this.hands.length; i++) {
      const slot = this.#layout.slots[i];
      if (slot === undefined) continue;
      // ★ 位次对得上就把上一帧的状态接过来（见 `CardRig.settleInherit`）
      this.#rig.settleInherit(i, slot, prevStates[i]);
    }
  }

  /** 这张牌在扇形里排第几。打出去的牌返回 -1 */
  #posOf(cardIndex: number): number {
    return this.#order.indexOf(cardIndex);
  }

  /** 出牌线：手牌顶边再往上 `playMargin`。牌的**目标位置**越过它就是要打出去 */
  #playLineY(): number {
    return this.#layout.centerY - this.#size.height / 2 - this.#playMargin * this.#size.height;
  }

  /** 拖动时这张牌该去哪（指针 + 抓住时的偏移） */
  #dragTarget(cardIndex: number): [number, number] {
    const g = this.#grab.get(cardIndex) ?? [0, 0];
    const p = this.#pointer ?? [0, 0];
    return [p[0] + g[0], p[1] + g[1]];
  }

  /**
   * 把这张牌插到离 `x` 最近的那一位上（**实时换位**，其余的牌会自己让开）。
   *
   * ⚠️ 挪的是 `#order`（位次表），不是 `hands` —— 卡牌节点和"摆在哪"是两件事。
   * 换完位之后被拖的那张牌的**槽位**变了，所以松手之后它会自己飞到新位置。
   */
  #reorderTo(cardIndex: number, x: number): void {
    const from = this.#order.indexOf(cardIndex);
    if (from < 0) return;
    const slots = this.#layout.slots;
    let best = from;
    let bestD = Infinity;
    for (let i = 0; i < slots.length; i++) {
      const d = Math.abs((slots[i]?.x ?? 0) - x);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    if (best === from) return;
    this.#order.splice(from, 1);
    this.#order.splice(best, 0, cardIndex);
  }

  // ---- 内部：输入 ----

  #attach(): void {
    const input = this.#input;
    input.on('globalpointermove', this.#onMove, this);
    input.on('pointerdown', this.#onDown, this);
    input.on('pointerup', this.#onUp, this);
    input.on('pointerupoutside', this.#onUp, this);
  }

  #detach(): void {
    const input = this.#input;
    input.off('globalpointermove', this.#onMove, this);
    input.off('pointerdown', this.#onDown, this);
    input.off('pointerup', this.#onUp, this);
    input.off('pointerupoutside', this.#onUp, this);
  }

  /** 事件坐标 → 本容器坐标 */
  #local(e: FederatedPointerEvent): [number, number] {
    const p = this.view.toLocal(e.global);
    return [p.x, p.y];
  }

  readonly #onMove = (e: FederatedPointerEvent): void => {
    this.#pointer = this.#local(e);
    if (this.#pressedCard >= 0) {
      const dx = this.#pointer[0] - this.#downAt[0];
      const dy = this.#pointer[1] - this.#downAt[1];
      if (exceedsDragSlop(dx, dy)) this.#dragged = true;
    }
    // ⚠️ 兜底：在画布外松开鼠标时 `pointerup` 收不到，牌会一直粘在指针上。
    //    指针**没有按键**却又收到移动，就当它已经松开了。
    //    （`buttons` 是原生 PointerEvent 的位掩码，0 = 一个键都没按）
    if ((this.#pressedCard >= 0 || this.#dragged) && e.buttons === 0) this.#release();
  };

  readonly #onDown = (e: FederatedPointerEvent): void => {
    this.#pointer = this.#local(e);
    this.#downAt = [this.#pointer[0], this.#pointer[1]];
    this.#dragged = false;
    this.#grab.clear();
    this.#dragSet = [];
    // 新的一次按下：清掉上一次长按钉住的说明（点别处 = 关闭）
    this.#pinnedCard = -1;
    this.#pressHoldMs = 0;
    this.#longPressed = false;

    // ⚠️ 不可操作时**连"选中 / 拖拽"都不该发生**（评审：多选不受规则限制）。
    //    `setInteractive(false)` 原来只挡 hover，`#onDown`/`#release` 照选照拖。
    if (!this.#interactive) {
      this.#pressedCard = -1;
      return;
    }

    const hit = this.#hitTest(this.#pointer);
    this.#pressedCard = hit;
    if (hit < 0) return;

    // 拖的是**选中组**还是单张：抓住一张已经选中的、而且选中不止一张 → 带整组
    const group =
      this.#selected.has(hit) && this.#selected.size > 1
        ? [...this.#selected].sort((a, b) => a - b)
        : [hit];
    this.#dragSet = group.filter((i) => !this.#played.has(i));

    for (const i of this.#dragSet) {
      const card = this.hands[i];
      if (card === undefined) continue;
      // ⚠️ 用**当前动画位置**算偏移：用槽位算的话，牌还在半路上时一抓就会跳过去
      this.#grab.set(i, [card.pose.x - this.#pointer[0], card.pose.y - this.#pointer[1]]);
    }
  };

  readonly #onUp = (): void => {
    this.#release();
  };

  /** 松手：分清"点选"和"拖出去打牌" */
  #release(): void {
    const hit = this.#pressedCard;
    const wasDragged = this.#dragged;
    const dragSet = [...this.#dragSet];
    // 长按过的这一下是「看说明」，不该再算成点选
    const longPressed = this.#longPressed;
    this.#longPressed = false;

    // ⚠️ "拖出去了没有"要用**目标位置**（指针 + 抓住时的偏移）判，
    //    不是当前动画位置 —— 甩得快的牌还在半路上就已经松手了，
    //    按当前位置判会漏掉这一下。所以必须在清 `#grab` **之前**算
    // ★ 整组一起判（outIndices）：只要一张过线，整组都算"打出去"。
    const out = outIndices(dragSet, (i) => this.#dragTarget(i)[1], this.#playLineY());

    this.#pressedCard = -1;
    this.#dragged = false;
    this.#grab.clear();
    this.#dragSet = [];

    if (hit < 0) {
      // 点在空白处：清空选中
      if (!wasDragged && this.#clearOnEmptyClick) this.#selected.clear();
      return;
    }
    if (!wasDragged) {
      // **点一下 = 选中，再点一下 = 取消选中**（长按看说明的那一下不算）
      if (!longPressed) this.toggleSelect(hit);
      return;
    }
    // ★ 不能出牌时（比如刚打出一对、正被人抽牌），只允许换位：拖过线也弹回来
    if (out.length > 0 && this.#playable) this.#play(out);
  }

  /**
   * 打出去。
   *
   * ⚠️ 这里**只是**把牌从顺序表里摘掉、标记成 played、让它停在落点 ——
   * 手牌真正的增减由上层状态驱动（`architecture.md` §7.4 的 keyed diff）。
   * `onPlay` 是给上层的**通知**，不是让上层在这里重建手牌的邀请。
   */
  #play(cards: readonly number[]): void {
    // ★ 先把"打出去之前"记下来（`revert()` 的唯一依据，见它）
    const undo: { order: number[]; played: number[]; z: Array<[number, number]> } = {
      order: [...this.#order],
      played: [...this.#played],
      z: [],
    };

    const defs: CardDef[] = [];
    for (const i of cards) {
      if (this.#played.has(i)) continue;
      this.#played.add(i);
      this.#selected.delete(i);
      const pos = this.#order.indexOf(i);
      if (pos >= 0) this.#order.splice(pos, 1);
      const def = this.#defs[i];
      if (def !== undefined) defs.push(def);
      // 停在那儿，并且压到手牌**下面** —— 已经打出去的牌不该盖住手里的
      const card = this.hands[i];
      if (card !== undefined) {
        undo.z.push([i, card.zIndex]);
        card.zIndex = -1;
      }
    }
    // ⚠️ 两步出牌（先拖一组、再拖一张定牌名 / 目标）会进这里两次。
    //    不能覆盖第一次的快照，否则 `revert()` 回到的是"第一组已经打出去"的那一帧，
    //    那组牌就永远回不到手牌里（评审报的"多选之后牌消失"）。
    if (this.#undo === null) this.#undo = undo;
    if (defs.length > 0) this.#onPlay?.(defs);
  }

  /**
   * 点在哪个四边形里。**从最上面一张往下找** —— 必须和看到的层叠顺序一致，
   * 否则把牌拖到另一张上面时抓到的是下面那张。
   */
  #hitTest(at: readonly [number, number] | null): number {
    if (at === null) return -1;
    const p: Pt = { x: at[0], y: at[1] };

    const order = this.hands
      .map((_, i) => i)
      .filter((i) => !this.#played.has(i) && !this.#hidden.has(i));
    order.sort((a, b) => (this.hands[b]?.zIndex ?? 0) - (this.hands[a]?.zIndex ?? 0));

    for (const i of order) {
      const card = this.hands[i];
      if (card === undefined) continue;
      const pose = card.pose;
      const s = pose.scale;
      const c = card.corners;
      // ⚠️ `corners` 是**相对牌中心**的、而且不含 `scale`（缩放走容器的 `scale`），
      //    所以世界坐标是 `中心 + scale × 角`。少了这个乘法，倾斜或放大之后
      //    就会"看着在那儿、点不到"
      const quad: Pt[] = [
        { x: pose.x + c.x0 * s, y: pose.y + c.y0 * s },
        { x: pose.x + c.x1 * s, y: pose.y + c.y1 * s },
        { x: pose.x + c.x2 * s, y: pose.y + c.y2 * s },
        { x: pose.x + c.x3 * s, y: pose.y + c.y3 * s },
      ];
      if (pointInPoly(p, quad)) return i;
    }
    return -1;
  }
}
